import { GraphViewComponent } from "./components/graph-view/graph-view.component";
import * as d3 from 'd3';

import { Component, computed, DestroyRef, HostListener, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap/tooltip';

import { ApiService } from "./services/api.service";
import { ApiResponse, ComponentContextMenuRequest, ComponentSummary, FailureDetail, ComponentDetail, ComponentType, SafetyGoal, AnalysisRow, FailureEffectOption, SystemEffectOption, FailureInstanceOption, SafetyMechanismOption, CoverageLink, ThermalModelOption, StandardOption, IdSuggestions, WorksheetRow, GraphNode, GraphLink, GraphNodeOption, GraphRelationMeta, RemainingFailureModeOption, WorksheetSummary, TelesisNetlistUploadResult, SmartPdfUploadResult } from "./models";
import { StoreService } from "./services/store.service";
import { BusinessLogicService } from './services/business-logic.service';
import { WorksheetViewComponent } from './components/worksheet-view/worksheet-view.component';
import { PdfSchematicViewerComponent } from './components/pdf-schematic-viewer/pdf-schematic-viewer.component';
import { NetlistUploadComponent } from './components/forms/netlist-upload/netlist-upload.component';
import { SmartPdfUploadComponent } from './components/forms/smart-pdf-upload/smart-pdf-upload.component';
import { PdfAnnotationStateService } from './services/pdf-annotation-state.service';
import { FmedaPdfWindowSyncService } from './services/fmeda-pdf-window-sync.service';
import { WebsocketService, TimelineJournalEvent } from '../../websocket.service';
import config from '../../../../auth_config.json';

type ContextMenuActionId =
  | 'edit-component-temp'
  | 'assign-type'
  | 'add-failure-instance'
  | 'edit-coverage'
  | 'edit-fe-link'
  | 'edit-se-link'
  | 'edit-fi-cause';

type FmedaView = 'worksheet' | 'create' | 'edit' | 'graph';

type FmedaCreateTab =
  | 'component-types'
  | 'components'
  | 'failure-instances'
  | 'failure-links'
  | 'safety-mechanisms'
  | 'safety-goals'
  | 'system-effects'
  | 'failure-effects';

type CreateDialogMode = 'create' | 'edit';

type CreateEntityReturnContext =
  | { parent: 'add-failure-instance'; field: 'failureEffectId' | 'systemEffectId' }
  | { parent: 'create-failure-effect'; field: 'systemEffectId' };

@Component({
  selector: 'app-fmeda',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgbTooltipModule,
    WorksheetViewComponent,
    GraphViewComponent,
    PdfSchematicViewerComponent,
    NetlistUploadComponent,
    SmartPdfUploadComponent
  ],
  templateUrl: './fmeda.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './fmeda.component.css'
})

export class FmedaComponent implements OnInit {

  private readonly apiService = inject(ApiService);
  public readonly store = inject(StoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  public readonly businessLogic = inject(BusinessLogicService);
  private readonly pdfAnnotationStateService = inject(PdfAnnotationStateService);
  private readonly pdfWindowSyncService = inject(FmedaPdfWindowSyncService);
  private readonly apiBaseUrl = config.fmedaApiUri;
  private readonly websocketService = inject(WebsocketService);
  readonly currentProject = this.store.currentProject;

  readonly maxNetlistUploadSizeBytes = 10 * 1024 * 1024;
  readonly maxSmartPdfUploadSizeBytes = 50 * 1024 * 1024;
  readonly smartPdfUrl = signal('');
  readonly graphShowUnlinkedComponents = signal(false);
  readonly focusedComponentDesignator = signal('');
  readonly isPdfPopoutActive = signal(false);
  readonly showComponentContextMenu = signal(false);
  readonly showEditComponentTempDialog = signal(false);
  readonly showEditCoverageDialog = signal(false);
  readonly showEditFeLinkDialog = signal(false);
  readonly showEditSeLinkDialog = signal(false);
  readonly showEditFiCauseDialog = signal(false);
  readonly showEditFailureLinkDialog = signal(false);
  readonly showEditSafetyMechanismDialog = signal(false);
  readonly showCreateEntityDialog = signal(false);
  readonly showAssignTypeDialog = signal(false);
  readonly showAddFailureInstanceDialog = signal(false);
  readonly contextMenuPosition = signal({ x: 0, y: 0 });
  readonly contextMenuComponentDesignator = signal('');
  readonly contextMenuActions = signal<Array<{ id: ContextMenuActionId; label: string }>>([]);

  private pdfPopoutWindow: Window | null = null;
  private createEntityReturnContext: CreateEntityReturnContext | null = null;

  activeView: FmedaView = 'worksheet';
  activeCreateTab: FmedaCreateTab = 'components';
  createDialogMode: CreateDialogMode = 'create';
  createListSearch = '';
  createListSortDirection: 'asc' | 'desc' = 'asc';

  showClearDbModal = false;
  clearSelections = {
    Project: true,
    Component: true,
    FailureInstance: true,
    FailureEffect: true,
    SystemEffect: true,
    SafetyGoal: true,
    SafetyMechanism: true
  };
  clearConfirmation = false;

  private createFailureModeFormGroup(initial?: {
    code?: string;
    failureModeName?: string;
    thermalModelId?: string;
    baseRateValue?: number;
    baseRateUnit?: string;
    standardName?: string;
  }) {
    return new FormGroup({
      code: new FormControl(initial?.code ?? '', { nonNullable: true }),
      failureModeName: new FormControl(initial?.failureModeName ?? '', { nonNullable: true }),
      thermalModelId: new FormControl(initial?.thermalModelId ?? '', { nonNullable: true }),
      baseRateValue: new FormControl(initial?.baseRateValue ?? 1, { nonNullable: true }),
      baseRateUnit: new FormControl(initial?.baseRateUnit ?? 'FIT', { nonNullable: true }),
      standardName: new FormControl(initial?.standardName ?? '', { nonNullable: true })
    });
  }

  readonly createComponentForm = new FormGroup({
    componentDesignator: new FormControl('', { nonNullable: true }),
    componentTypeName: new FormControl('', { nonNullable: true }),
    opTemp: new FormControl(25, { nonNullable: true })
  });

  readonly createComponentTypeForm = new FormGroup({
    name: new FormControl('', { nonNullable: true }),
    failureModes: new FormArray([
      this.createFailureModeFormGroup()
    ])
  });

  readonly createFailureLinkForm = new FormGroup({
    componentDesignator: new FormControl('', { nonNullable: true }),
    failureModeCode: new FormControl('', { nonNullable: true }),
    failureEffectId: new FormControl('', { nonNullable: true }),
    fiId: new FormControl('', { nonNullable: true }),
    fiName: new FormControl('', { nonNullable: true }),
    systemEffectId: new FormControl('', { nonNullable: true })
  });

  readonly createFailureInstanceForm = new FormGroup({
    componentDesignator: new FormControl('', { nonNullable: true }),
    failureModeCode: new FormControl('', { nonNullable: true }),
    failureEffectId: new FormControl('', { nonNullable: true }),
    systemEffectId: new FormControl('', { nonNullable: true }),
    fiId: new FormControl('', { nonNullable: true }),
    fiName: new FormControl('', { nonNullable: true })
  });

  readonly createSafetyMechanismForm = new FormGroup({
    smId: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    failureInstanceId: new FormControl('', { nonNullable: true }),
    diagnosticCoverage: new FormControl(0.8, { nonNullable: true }),
    mechanismType: new FormControl('SPF', { nonNullable: true })
  });

  readonly createSafetyGoalForm = new FormGroup({
    id: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true })
  });

  readonly createSystemEffectForm = new FormGroup({
    id: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    safetyGoalId: new FormControl('', { nonNullable: true })
  });

  readonly createFailureEffectForm = new FormGroup({
    id: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    systemEffectId: new FormControl('', { nonNullable: true })
  });

  readonly editComponentForm = new FormGroup({
    componentDesignator: new FormControl('', { nonNullable: true }),
    opTemp: new FormControl(25, { nonNullable: true })
  });

  readonly assignTypeForm = new FormGroup({
    componentDesignator: new FormControl('', { nonNullable: true }),
    componentTypeName: new FormControl('', { nonNullable: true }),
    opTemp: new FormControl(25, { nonNullable: true })
  });

  readonly editCoverageForm = new FormGroup({
    coverageLinkKey: new FormControl('', { nonNullable: true }),
    safetyMechanismId: new FormControl('', { nonNullable: true }),
    failureInstanceId: new FormControl('', { nonNullable: true }),
    diagnosticCoverage: new FormControl(0.8, { nonNullable: true }),
    mechanismType: new FormControl<'SPF' | 'LATENT'>('SPF', { nonNullable: true })
  });

  readonly editFailureLinkForm = new FormGroup({
    failureInstanceId: new FormControl('', { nonNullable: true }),
    componentDesignator: new FormControl('', { nonNullable: true }),
    failureModeCode: new FormControl('', { nonNullable: true }),
    failureEffectId: new FormControl('', { nonNullable: true }),
    systemEffectId: new FormControl('', { nonNullable: true }),
    safetyGoalId: new FormControl('', { nonNullable: true })
  });

  readonly editFeLink = new FormGroup({
    failureEffectId: new FormControl('', { nonNullable: true }),
    systemEffectId: new FormControl('', { nonNullable: true })
  });

  readonly editSeLink = new FormGroup({
    systemEffectId: new FormControl('', { nonNullable: true }),
    safetyGoalId: new FormControl('', { nonNullable: true })
  });

  readonly editFiCause = new FormGroup({
    failureInstanceId: new FormControl('', { nonNullable: true }),
    failureEffectId: new FormControl('', { nonNullable: true })
  });

  readonly components = this.store.components;
  readonly selectedComponent = this.store.selectedComponent;
  readonly componentTypes = this.store.componentTypes;
  readonly safetyGoals = this.store.safetyGoals;
  readonly analysis = this.store.analysis;
  readonly failureEffects = this.store.failureEffects;
  readonly systemEffects = this.store.systemEffects;
  readonly failureInstances = this.store.failureInstances;
  readonly safetyMechanisms = this.store.safetyMechanisms;
  readonly coverageLinks = this.store.coverageLinks;
  readonly thermalModels = this.store.thermalModels;
  readonly standards = this.store.standards;
  readonly idSuggestions = this.store.idSuggestions;
  readonly selectedWorksheetRowId = this.store.selectedWorksheetRowId;
  readonly loading = this.store.loading;
  readonly error = this.store.error;
  readonly actionMessage = this.store.actionMessage;

  readonly worksheetRows = this.businessLogic.worksheetRows;
  readonly worksheetSummary = this.businessLogic.worksheetSummary;

  readonly graphData = computed(() => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const seen = new Set<string>();

    const addNode = (node: GraphNode) => {
      if (!seen.has(node.id)) {
        seen.add(node.id);
        nodes.push(node);
      }
    };

    // Components
    const linkedComponentDesignators = new Set<string>();
    for (const fi of this.failureInstances()) {
      const designatorLookup = fi.component_designator || (fi as any).component_id || (fi as any).componentId;
      if (designatorLookup) {
        linkedComponentDesignators.add(designatorLookup);
      }
    }

    for (const c of this.components()) {
      if (this.graphShowUnlinkedComponents() || linkedComponentDesignators.has(c.component_designator)) {
        addNode({ id: c.component_designator, label: c.component_designator, type: 'component' });
      }
    }

    // Failure instances + chains
    for (const fi of this.failureInstances()) {
      addNode({ id: fi.id, label: fi.name, type: 'failure_instance' });
      const visualSourceId = fi.component_designator || (fi as any).component_id || (fi as any).componentId;

      links.push({ source: visualSourceId, target: fi.id, relType: 'HAS_FAILURE' });

      if (fi.failure_effect_id) {
        addNode({ id: fi.failure_effect_id, label: fi.failure_effect_name ?? fi.failure_effect_id, type: 'failure_effect' });
        links.push({ source: fi.id, target: fi.failure_effect_id, relType: 'CAUSES' });
      }

      if (fi.system_effect_id) {
        addNode({ id: fi.system_effect_id, label: fi.system_effect_name ?? fi.system_effect_id, type: 'system_effect' });
      }

      if (fi.safety_goal_id) {
        addNode({ id: fi.safety_goal_id, label: fi.safety_goal_description ?? fi.safety_goal_id, type: 'safety_goal' });
      }

      for (const smId of fi.safety_mechanism_ids) {
        const smName = fi.safety_mechanism_names[fi.safety_mechanism_ids.indexOf(smId)] ?? smId;
        addNode({ id: smId, label: smName, type: 'safety_mechanism' });
        links.push({ source: fi.id, target: smId, relType: 'COVERED_BY' });
      }

      for (const smId of fi.latent_mechanism_ids) {
        const smName = fi.latent_mechanism_names[fi.latent_mechanism_ids.indexOf(smId)] ?? smId;
        addNode({ id: smId, label: smName, type: 'safety_mechanism' });
        if (!links.some(l => l.source === fi.id && l.target === smId)) {
          links.push({ source: fi.id, target: smId, relType: 'COVERED_BY_LATENT' });
        }
      }
    }

    // FailureEffect → SystemEffect edges
    for (const fe of this.failureEffects()) {
      if (fe.system_effect_id) {
        addNode({ id: fe.id, label: fe.name, type: 'failure_effect' });
        addNode({ id: fe.system_effect_id, label: fe.system_effect_name ?? fe.system_effect_id, type: 'system_effect' });
        links.push({ source: fe.id, target: fe.system_effect_id, relType: 'LEADS_TO' });
      }
    }

    // SystemEffect → SafetyGoal edges
    for (const se of this.systemEffects()) {
      if (se.safety_goal_id) {
        addNode({ id: se.id, label: se.name, type: 'system_effect' });
        addNode({ id: se.safety_goal_id, label: se.safety_goal_description ?? se.safety_goal_id, type: 'safety_goal' });
        links.push({ source: se.id, target: se.safety_goal_id, relType: 'VIOLATES' });
      }
    }

    return { nodes, links, meta: {} };
  });

  readonly unassignedComponents = computed(() => this.components().filter((c: ComponentSummary) => !c.component_type));
  readonly assignedComponents = computed(() => this.components().filter((c: ComponentSummary) => !!c.component_type));

  assignTypeSearch = '';
  selectedIdenticalComponentDesignators: string[] = [];

  constructor() {
    effect(() => {
      const designator = this.focusedComponentDesignator();
      this.pdfAnnotationStateService.highlightComponentInPdf(designator);
    }, { allowSignalWrites: true });
    //  Effect: Automatically manage WebSTOMP channel subscription per active project
    effect((onCleanup) => {
      const project = this.currentProject();
      if (!project?.id) return;

      const sub = this.websocketService.watchProjectEvents(project.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event: TimelineJournalEvent) => {
          if (!event) return;

          console.log(`⚡ [STOMP Event Received]:`, event);

          //  Push into Store Signal for real-time GitHub-style commit feed
          this.store.addTimelineEvent(event);

          // Alert user of remote change
          this.actionMessage.set(`Live update (${event.action}) from user ${event.triggered_by}`);

          // Automatically reload worksheet, graph, and menus
          this.refreshData();
        });
    //  Unsubscribe from the old project topic when project changes or component dies
   onCleanup(() => sub.unsubscribe());
    });
  }
  

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const requestedView = params.get('view');
        const requestedTab = params.get('tab');

        if (this.isFmedaView(requestedView)) {
          this.activeView = requestedView;
        }

        if (this.isFmedaCreateTab(requestedTab)) {
          this.activeCreateTab = requestedTab;

          if (requestedView === null) {
            this.activeView = 'create';
          }
        }
      });

    this.assignTypeForm.controls.componentDesignator.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onAssignComponentSelectionChange();
      });

    // Listen for selections coming FROM the PDF viewer via the service
    this.pdfAnnotationStateService.componentToSelect$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(componentDesignator => {
        if (componentDesignator) {
          this.updateFocusedComponentDesignator(componentDesignator);
          this.actionMessage.set('');
          this.pdfWindowSyncService.publishFocus(componentDesignator);
        }
      });

    this.pdfWindowSyncService.focusFromRemote$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(componentDesignator => {
        if (componentDesignator) {
          this.updateFocusedComponentDesignator(componentDesignator);
          this.actionMessage.set('');
        }
      });

    this.pdfWindowSyncService.popoutOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isOpen => {
        this.isPdfPopoutActive.set(isOpen);

        if (!isOpen) {
          this.pdfPopoutWindow = null;
        }
      });

    this.refreshData();
  }

  // Undo Trigger Function
  onUndo(): void {
    this.store.triggerUndo(() => {
      this.refreshData();
    });
  }

  // Intercept Ctrl+Z or Cmd+Z globally across the workspace
  @HostListener('window:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const modifier = isMac ? event.metaKey : event.ctrlKey;

    if (modifier && event.key.toLowerCase() === 'z' && !event.shiftKey) {
      const target = event.target as HTMLElement;
      // Do not trigger the undo if typing in a form 
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      event.preventDefault();
      this.onUndo();
    }
  }

  setView(view: FmedaView): void {
    this.activeView = view;
    this.syncRouteState();
    this.actionMessage.set('');
  }

  setCreateTab(tab: FmedaCreateTab): void {
    this.activeCreateTab = tab;
    this.syncRouteState();
    this.actionMessage.set('');
  }

  private syncRouteState(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        view: this.activeView,
        tab: this.activeView === 'create' ? this.activeCreateTab : null
      },
      replaceUrl: true
    });
  }

  private isFmedaView(value: string | null): value is FmedaView {
    return value === 'worksheet' || value === 'create' || value === 'edit' || value === 'graph';
  }

  private isFmedaCreateTab(value: string | null): value is FmedaCreateTab {
    return value === 'component-types'
      || value === 'components'
      || value === 'failure-instances'
      || value === 'failure-links'
      || value === 'safety-mechanisms'
      || value === 'safety-goals'
      || value === 'system-effects'
      || value === 'failure-effects';
  }

  onCreateListSearchChange(value: string): void {
    this.createListSearch = value;
  }

  toggleCreateListSortDirection(): void {
    this.createListSortDirection = this.createListSortDirection === 'asc' ? 'desc' : 'asc';
  }

  refreshData(): void {
    this.loading.set(true);
    this.error.set('');
    this.actionMessage.set('');

    this.apiService.getComponents().subscribe({
      next: (response: ApiResponse<ComponentSummary[]>) => {
        this.components.set(response.data);
        this.reconcileAssignSelection();
        this.loading.set(false);
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to load components.');
        this.loading.set(false);
      }
    });

    this.apiService.getComponentTypes().subscribe({
      next: (response: ApiResponse<ComponentType[]>) => { this.componentTypes.set(response.data); }
    });

    this.apiService.getSafetyGoals().subscribe({
      next: (response: ApiResponse<SafetyGoal[]>) => { this.safetyGoals.set(response.data); }
    });

    this.apiService.getAnalysis().subscribe({
      next: (response: ApiResponse<AnalysisRow[]>) => { this.analysis.set(response.data); }
    });

    this.apiService.getFailureEffects().subscribe({
      next: (response: ApiResponse<FailureEffectOption[]>) => { this.failureEffects.set(response.data); }
    });

    this.apiService.getSystemEffects().subscribe({
      next: (response: ApiResponse<SystemEffectOption[]>) => { this.systemEffects.set(response.data); }
    });

    this.apiService.getFailureInstances().subscribe({
      next: (response: ApiResponse<FailureInstanceOption[]>) => { this.failureInstances.set(response.data); }
    });

    this.apiService.getSafetyMechanisms().subscribe({
      next: (response: ApiResponse<SafetyMechanismOption[]>) => { this.safetyMechanisms.set(response.data); }
    });

    this.apiService.getCoverageLinks().subscribe({
      next: (response: ApiResponse<CoverageLink[]>) => { this.coverageLinks.set(response.data); }
    });

    this.apiService.getThermalModels().subscribe({
      next: (response: ApiResponse<ThermalModelOption[]>) => { this.thermalModels.set(response.data); }
    });

    this.apiService.getStandards().subscribe({
      next: (response: ApiResponse<StandardOption[]>) => { this.standards.set(response.data); }
    });

    this.apiService.getIdSuggestions().subscribe({
      next: (response: ApiResponse<IdSuggestions>) => {
        this.idSuggestions.set(response.data);
        this.applySuggestedIds();
      }
    });
  }


  seedDemoData(): void {
    this.loading.set(true);
    this.apiService.seedDemoData().subscribe({
      next: () => {
        this.actionMessage.set('Seed complete.');
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Seed failed.');
        this.loading.set(false);
      }
    });
  }

  openClearDbModal(): void {
    this.showClearDbModal = true;
    this.clearConfirmation = false;
    this.clearSelections = {
      Project: true,
      Component: true,
      FailureInstance: true,
      FailureEffect: true,
      SystemEffect: true,
      SafetyGoal: true,
      SafetyMechanism: true
    };
  }

  hasClearSelection(): boolean {
    return Object.values(this.clearSelections).some(val => val);
  }

  confirmClearDatabase(): void {
    const selectedTypes = Object.entries(this.clearSelections)
      .filter(([_, isSelected]) => isSelected)
      .map(([type, _]) => type);

    if (selectedTypes.length === 0) return;

    this.showClearDbModal = false;
    this.loading.set(true);
    this.apiService.clearDatabase(selectedTypes).subscribe({
      next: () => {
        this.actionMessage.set('Selected data cleared.');
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Clear failed.');
        this.loading.set(false);
      }
    });
  }

  onNetlistUploadStarted(filename: string): void {
    this.error.set('');
    this.actionMessage.set(`Uploading ${filename}...`);
  }

  onNetlistUploadSucceeded(data: TelesisNetlistUploadResult): void {
    this.actionMessage.set(
      `Imported ${data.filename}: ${data.components_parsed} components, ${data.nets_parsed} nets.`
    );
    this.refreshData();
  }

  onNetlistUploadFailed(message: string): void {
    this.error.set(message);
    this.actionMessage.set('');
  }

  onSmartPdfUploadStarted(filename: string): void {
    this.error.set('');
    this.actionMessage.set(`Uploading PDF ${filename}...`);
  }

  onSmartPdfUploadSucceeded(data: SmartPdfUploadResult): void {
    this.smartPdfUrl.set(`${this.apiBaseUrl}${data.url}`);
    this.actionMessage.set(`Loaded schematic ${data.filename}. Click a worksheet row to search by designator.`);

    //  Convert the incoming snake_case keys into your strict TypeScript camelCase fields
    const formattedAnnotations = (data.annotations || []).map((annot: any) => ({
      componentDesignator: annot.component_designator, // Maps 'component_designator' ➔ 'componentDesignator'
      page: annot.page,
      xref: annot.xref,
      rect: annot.rect
    }));

    // Pass the newly structured camelCase data to your streams
    this.pdfAnnotationStateService.loadAnnotations(formattedAnnotations);
    this.publishPdfWindowSnapshot(formattedAnnotations);
  }

  onSmartPdfUploadFailed(message: string): void {
    this.error.set(message);
    this.actionMessage.set('');
  }

  onWorksheetComponentSelected(componentDesignator: string): void {
    this.updateFocusedComponentDesignator(componentDesignator);
    this.actionMessage.set('');
    this.pdfWindowSyncService.publishFocus(componentDesignator);
    console.log(`Component selected in worksheet: ${componentDesignator}`);
  }

  onGraphComponentSelected(componentDesignator: string): void {
    this.updateFocusedComponentDesignator(componentDesignator);
    this.pdfWindowSyncService.publishFocus(componentDesignator);
  }

  onComponentContextMenuRequested(event: ComponentContextMenuRequest): void {
    this.closeComponentContextMenu();
    this.updateFocusedComponentDesignator(event.componentDesignator);
    this.pdfWindowSyncService.publishFocus(event.componentDesignator);
    this.contextMenuComponentDesignator.set(event.componentDesignator);

    if (event.failureModeCode) {
      this.openAddFailureInstanceDialog(event.componentDesignator, event.failureModeCode);
      return;
    }

    const component = this.components().find((entry) => entry.component_designator === event.componentDesignator);
    const actions: Array<{ id: ContextMenuActionId; label: string }> = [
      { id: 'edit-component-temp', label: 'Edit temperature' }
    ];

    if (!component?.component_type) {
      actions.push({ id: 'assign-type', label: 'Assign type' });
    }

    if (this.businessLogic.getRemainingFailureModeCount(event.componentDesignator) > 0) {
      actions.push({ id: 'add-failure-instance', label: 'Add failure instance' });
    }

    this.openContextMenuAt(event.clientX, event.clientY, actions);
  }

  onAssignableComponentContextMenu(event: MouseEvent, componentDesignator: string): void {
    event.preventDefault();
    this.selectAssignableComponent(componentDesignator);
    this.contextMenuComponentDesignator.set(componentDesignator);
    this.openContextMenuAt(event.clientX, event.clientY, [
      { id: 'edit-component-temp', label: 'Edit temperature' }
    ]);
  }

  onCoverageContextMenu(event: MouseEvent): void {
    event.preventDefault();
    if (!this.editCoverageForm.controls.coverageLinkKey.value) {
      this.error.set('Select a coverage link first.');
      return;
    }

    this.syncCoverageSelection();
    this.openContextMenuAt(event.clientX, event.clientY, [
      { id: 'edit-coverage', label: 'Edit Diagnostic Coverage' }
    ]);
  }

  onFailureEffectLinkContextMenu(event: MouseEvent): void {
    event.preventDefault();
    if (!this.editFeLink.controls.failureEffectId.value) {
      this.error.set('Select a failure effect first.');
      return;
    }

    this.syncFeLinkSelection();
    this.openContextMenuAt(event.clientX, event.clientY, [
      { id: 'edit-fe-link', label: 'Edit Failure Effect → System Effect' }
    ]);
  }

  onSystemEffectLinkContextMenu(event: MouseEvent): void {
    event.preventDefault();
    if (!this.editSeLink.controls.systemEffectId.value) {
      this.error.set('Select a system effect first.');
      return;
    }

    this.syncSeLinkSelection();
    this.openContextMenuAt(event.clientX, event.clientY, [
      { id: 'edit-se-link', label: 'Edit System Effect → Safety Goal' }
    ]);
  }

  onFailureInstanceCauseContextMenu(event: MouseEvent): void {
    event.preventDefault();
    if (!this.editFiCause.controls.failureInstanceId.value) {
      this.error.set('Select a failure instance first.');
      return;
    }

    this.syncFiCauseSelection();
    this.openContextMenuAt(event.clientX, event.clientY, [
      { id: 'edit-fi-cause', label: 'Edit Failure Instance → Failure Effect' }
    ]);
  }

  onContextMenuActionSelected(actionId: ContextMenuActionId): void {
    if (actionId === 'edit-component-temp') {
      this.openEditComponentTempDialog();
      return;
    }

    if (actionId === 'assign-type') {
      this.openAssignTypeDialog();
      return;
    }

    if (actionId === 'add-failure-instance') {
      this.openAddFailureInstanceDialog();
      return;
    }

    if (actionId === 'edit-coverage') {
      this.openEditCoverageDialog();
      return;
    }

    if (actionId === 'edit-fe-link') {
      this.openEditFeLinkDialog();
      return;
    }

    if (actionId === 'edit-se-link') {
      this.openEditSeLinkDialog();
      return;
    }

    this.openEditFiCauseDialog();
  }

  private openContextMenuAt(clientX: number, clientY: number, actions: Array<{ id: ContextMenuActionId; label: string }>): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = 320;
    const menuHeight = actions.length * 44 + 12;
    this.contextMenuActions.set(actions);
    this.contextMenuPosition.set({
      x: Math.min(clientX, Math.max(16, viewportWidth - menuWidth - 16)),
      y: Math.min(clientY, Math.max(16, viewportHeight - menuHeight - 16))
    });
    this.showComponentContextMenu.set(true);
  }

  openEditComponentTempDialog(componentDesignator = this.contextMenuComponentDesignator()): void {
    const targetDesignator = componentDesignator.trim();
    if (!targetDesignator) {
      return;
    }

    const component = this.components().find((entry) => entry.component_designator === targetDesignator);
    this.editComponentForm.reset({
      componentDesignator: targetDesignator,
      opTemp: component?.op_temp ?? 25
    });
    this.showEditComponentTempDialog.set(true);
    this.closeComponentContextMenu();
  }

  openCreateEntityDialog(): void {
    this.createEntityReturnContext = null;
    this.createDialogMode = 'create';
    this.showCreateEntityDialog.set(true);
  }

  openCreateComponentEditDialog(componentDesignator: string): void {
    const targetDesignator = componentDesignator.trim();
    const component = this.components().find((entry) => entry.component_designator === targetDesignator);
    if (!component) {
      return;
    }

    this.createDialogMode = 'edit';
    this.selectedIdenticalComponentDesignators = [];
    this.assignTypeForm.reset({
      componentDesignator: targetDesignator,
      componentTypeName: component.component_type ?? '',
      opTemp: component.op_temp ?? 25
    });
    this.showCreateEntityDialog.set(true);
  }

  closeCreateEntityDialog(): void {
    const returnContext = this.createEntityReturnContext;
    this.createEntityReturnContext = null;

    if (returnContext?.parent === 'add-failure-instance') {
      this.showCreateEntityDialog.set(false);
      this.showAddFailureInstanceDialog.set(true);
      this.createDialogMode = 'create';
      return;
    }

    if (returnContext?.parent === 'create-failure-effect') {
      this.activeCreateTab = 'failure-effects';
      this.createDialogMode = 'create';
      this.showCreateEntityDialog.set(true);
      return;
    }

    this.showCreateEntityDialog.set(false);
    this.createDialogMode = 'create';
  }

  openAssignTypeDialog(componentDesignator = this.contextMenuComponentDesignator()): void {
    const targetDesignator = componentDesignator.trim();
    if (!targetDesignator) {
      return;
    }

    const component = this.components().find((entry) => entry.component_designator === targetDesignator);
    if (!component) {
      return;
    }

    this.selectedIdenticalComponentDesignators = [];
    this.assignTypeForm.reset({
      componentDesignator: targetDesignator,
      componentTypeName: '',
      opTemp: component.op_temp ?? 25
    });
    this.showAssignTypeDialog.set(true);
    this.closeComponentContextMenu();
  }

  closeAssignTypeDialog(): void {
    this.showAssignTypeDialog.set(false);
  }

  openAddFailureInstanceDialog(componentDesignator = this.contextMenuComponentDesignator(), preferredFailureModeCode = ''): void {
    const targetDesignator = componentDesignator.trim();
    if (!targetDesignator) {
      return;
    }

    this.createFailureInstanceForm.reset({
      componentDesignator: targetDesignator,
      failureModeCode: '',
      failureEffectId: '',
      systemEffectId: '',
      fiId: this.idSuggestions().failure_instance_id,
      fiName: ''
    });
    this.syncFailureInstanceModeOptions();

    const requestedModeCode = preferredFailureModeCode.trim();
    if (requestedModeCode) {
      const remainingModes = this.businessLogic.getRemainingFailureModesForComponent(targetDesignator);
      if (remainingModes.some((mode) => mode.code === requestedModeCode)) {
        this.createFailureInstanceForm.patchValue({ failureModeCode: requestedModeCode });
      }
    }

    this.onCreateFailureInstanceModeChanged();
    this.showAddFailureInstanceDialog.set(true);
    this.closeComponentContextMenu();
  }

  closeAddFailureInstanceDialog(): void {
    this.showAddFailureInstanceDialog.set(false);
  }

  onCreateFailureInstanceFailureEffectChanged(): void {
    const failureEffectId = this.createFailureInstanceForm.controls.failureEffectId.value;
    const selectedEffect = this.failureEffects().find((effect: FailureEffectOption) => effect.id === failureEffectId);
    this.createFailureInstanceForm.patchValue({ systemEffectId: selectedEffect?.system_effect_id ?? '' });
  }

  openCreateFailureEffectDialogFromFailureInstance(): void {
    this.showAddFailureInstanceDialog.set(false);
    this.createEntityReturnContext = { parent: 'add-failure-instance', field: 'failureEffectId' };
    this.createDialogMode = 'create';
    this.activeCreateTab = 'failure-effects';
    this.showCreateEntityDialog.set(true);
  }

  openCreateSystemEffectDialogFromFailureInstance(): void {
    this.showAddFailureInstanceDialog.set(false);
    this.createEntityReturnContext = { parent: 'add-failure-instance', field: 'systemEffectId' };
    this.createDialogMode = 'create';
    this.activeCreateTab = 'system-effects';
    this.showCreateEntityDialog.set(true);
  }

  openCreateSystemEffectDialogFromFailureEffect(): void {
    this.createEntityReturnContext = { parent: 'create-failure-effect', field: 'systemEffectId' };
    this.createDialogMode = 'create';
    this.activeCreateTab = 'system-effects';
    this.showCreateEntityDialog.set(true);
  }

  closeEditComponentTempDialog(): void {
    this.showEditComponentTempDialog.set(false);
  }

  openEditCoverageDialog(coverageLinkKey = ''): void {
    if (coverageLinkKey) {
      this.editCoverageForm.patchValue({ coverageLinkKey });
      this.syncCoverageSelection();
    }
    this.showEditCoverageDialog.set(true);
    this.closeComponentContextMenu();
  }

  closeEditCoverageDialog(): void {
    this.showEditCoverageDialog.set(false);
  }

  openEditFailureLinkDialog(failureInstanceId: string): void {
    const failureInstance = this.failureInstances().find((entry: FailureInstanceOption) => entry.id === failureInstanceId);
    if (!failureInstance) {
      return;
    }

    this.editFailureLinkForm.reset({
      failureInstanceId: failureInstance.id,
      componentDesignator: failureInstance.component_designator,
      failureModeCode: failureInstance.failure_mode_code,
      failureEffectId: failureInstance.failure_effect_id ?? '',
      systemEffectId: failureInstance.system_effect_id ?? '',
      safetyGoalId: failureInstance.safety_goal_id ?? ''
    });
    this.showEditFailureLinkDialog.set(true);
    this.closeComponentContextMenu();
  }

  closeEditFailureLinkDialog(): void {
    this.showEditFailureLinkDialog.set(false);
  }

  openEditSafetyMechanismDialog(mechanismId: string): void {
    const matchingLink = this.coverageLinks().find((link: CoverageLink) => link.safety_mechanism_id === mechanismId);
    if (!matchingLink) {
      return;
    }

    this.editCoverageForm.reset({
      coverageLinkKey: `${matchingLink.safety_mechanism_id}::${matchingLink.failure_instance_id}`,
      safetyMechanismId: matchingLink.safety_mechanism_id,
      failureInstanceId: matchingLink.failure_instance_id,
      diagnosticCoverage: matchingLink.diagnostic_coverage,
      mechanismType: matchingLink.mechanism_type
    });
    this.showEditSafetyMechanismDialog.set(true);
    this.closeComponentContextMenu();
  }

  closeEditSafetyMechanismDialog(): void {
    this.showEditSafetyMechanismDialog.set(false);
  }

  openEditFeLinkDialog(failureEffectId = ''): void {
    if (failureEffectId) {
      this.editFeLink.patchValue({ failureEffectId });
      this.syncFeLinkSelection();
    }
    this.showEditFeLinkDialog.set(true);
    this.closeComponentContextMenu();
  }

  closeEditFeLinkDialog(): void {
    this.showEditFeLinkDialog.set(false);
  }

  openEditSeLinkDialog(systemEffectId = ''): void {
    if (systemEffectId) {
      this.editSeLink.patchValue({ systemEffectId });
      this.syncSeLinkSelection();
    }
    this.showEditSeLinkDialog.set(true);
    this.closeComponentContextMenu();
  }

  closeEditSeLinkDialog(): void {
    this.showEditSeLinkDialog.set(false);
  }

  openEditFiCauseDialog(failureInstanceId = ''): void {
    if (failureInstanceId) {
      this.editFiCause.patchValue({ failureInstanceId });
      this.syncFiCauseSelection();
    }
    this.showEditFiCauseDialog.set(true);
    this.closeComponentContextMenu();
  }

  openCreateListFailureInstanceEditor(failureInstanceId: string): void {
    this.openEditFiCauseDialog(failureInstanceId);
  }

  openCreateListFailureLinkEditor(failureInstanceId: string): void {
    this.openEditFailureLinkDialog(failureInstanceId);
  }

  openCreateListSafetyMechanismEditor(mechanismId: string): void {
    this.openEditSafetyMechanismDialog(mechanismId);
  }

  openCreateListSystemEffectEditor(systemEffectId: string): void {
    this.openEditSeLinkDialog(systemEffectId);
  }

  openCreateListFailureEffectEditor(failureEffectId: string): void {
    this.openEditFeLinkDialog(failureEffectId);
  }

  isCreateTabEditSupported(tab: FmedaCreateTab): boolean {
    return tab === 'components'
      || tab === 'failure-instances'
      || tab === 'failure-links'
      || tab === 'safety-mechanisms'
      || tab === 'system-effects'
      || tab === 'failure-effects';
  }

  getActiveCreateTabHeading(): string {
    switch (this.activeCreateTab) {
      case 'component-types':
        return 'Component Types';
      case 'components':
        return 'Components';
      case 'failure-instances':
        return 'Failure Instances';
      case 'failure-links':
        return 'Failure Links';
      case 'safety-mechanisms':
        return 'Safety Mechanisms';
      case 'safety-goals':
        return 'Safety Goals';
      case 'system-effects':
        return 'System Effects';
      case 'failure-effects':
        return 'Failure Effects';
    }
  }

  getCreateDialogHeading(): string {
    if (this.createDialogMode === 'edit' && this.activeCreateTab === 'components') {
      return 'Edit Component';
    }

    return `Add ${this.getActiveCreateTabHeading().slice(0, -1)}`;
  }

  closeEditFiCauseDialog(): void {
    this.showEditFiCauseDialog.set(false);
  }

  syncEditFailureLinkFailureEffect(): void {
    const failureEffectId = this.editFailureLinkForm.controls.failureEffectId.value;
    const selectedEffect = this.failureEffects().find((effect: FailureEffectOption) => effect.id === failureEffectId);
    const systemEffectId = selectedEffect?.system_effect_id ?? '';
    const selectedSystemEffect = this.systemEffects().find((effect: SystemEffectOption) => effect.id === systemEffectId);

    this.editFailureLinkForm.patchValue({
      systemEffectId,
      safetyGoalId: selectedSystemEffect?.safety_goal_id ?? ''
    });
  }

  syncEditFailureLinkSystemEffect(): void {
    const systemEffectId = this.editFailureLinkForm.controls.systemEffectId.value;
    const selectedSystemEffect = this.systemEffects().find((effect: SystemEffectOption) => effect.id === systemEffectId);
    this.editFailureLinkForm.patchValue({ safetyGoalId: selectedSystemEffect?.safety_goal_id ?? '' });
  }

  closeComponentContextMenu(): void {
    this.showComponentContextMenu.set(false);
    this.contextMenuActions.set([]);
  }

  openPdfPopout(): void {
    this.publishPdfWindowSnapshot();

    if (this.pdfPopoutWindow && !this.pdfPopoutWindow.closed) {
      this.pdfPopoutWindow.focus();
      return;
    }

    const popupParams = new URLSearchParams();
    const pdfUrl = this.smartPdfUrl().trim();
    const focusedDesignator = this.focusedComponentDesignator().trim();

    if (pdfUrl) {
      popupParams.set('pdfUrl', pdfUrl);
    }

    if (focusedDesignator) {
      popupParams.set('designator', focusedDesignator);
    }

    const popupUrl = `${window.location.origin}${window.location.pathname}#/fmeda-pdf${popupParams.size ? `?${popupParams.toString()}` : ''}`;
    this.pdfPopoutWindow = window.open(
      popupUrl,
      'fmeda-pdf-popout',
      'popup=yes,width=1440,height=960,resizable=yes,scrollbars=yes'
    );

    if (!this.pdfPopoutWindow) {
      this.error.set('Unable to open PDF pop-out window. Check the browser popup blocker.');
      return;
    }

    this.pdfPopoutWindow.focus();
  }

  onGraphLinkCreated(event: { source_id: string; target_id: string; rel_type: string }): void {
    console.log('%c[DEBUG] AppComponent: Received onGraphLinkCreated event', 'color: green; font-weight: bold;', event);
    this.apiService.createLink(event).subscribe({
      next: () => {
        console.log('[DEBUG] AppComponent: createLink API call succeeded. Refreshing data.');
        this.actionMessage.set(`Link ${event.rel_type}: ${event.source_id} → ${event.target_id} created.`);
        this.refreshData();
      },
      error: (err: unknown) => {
        console.error('[DEBUG] AppComponent: createLink API call failed', err);
        this.error.set(err instanceof Error ? err.message : 'Failed to create link.');
      }
    });
  }

  onGraphLinkDeleted(event: { source_id: string; target_id: string; rel_type: string }): void {
    // Placeholder for future implementation
    console.warn('Link deletion requested but not yet implemented:', event);
  }

  onGraphViewConfigChanged(): void {
    this.graphShowUnlinkedComponents.update(v => !v);
  }

  onFailureEffectCreated(payload: { name: string; system_effect_id: string; id?: string }): void {
    this.apiService.createFailureEffect(payload).subscribe({
      next: (response) => {
        this.actionMessage.set(`Failure effect ${response.data.id} created.`);
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create failure effect.');
      }
    });
  }

  onFailureInstanceRequested(event: { componentDesignator: string; mode: RemainingFailureModeOption }): void {
    const { componentDesignator, mode } = event;
    const payload = {
      component_designator: componentDesignator,
      failure_mode_code: mode.code,
      fi_name: this.businessLogic.buildFailureInstanceName(componentDesignator, mode.code)
    };

    this.apiService.createFailureInstance(payload).subscribe({
      next: () => {
        this.actionMessage.set(`Failure instance for ${componentDesignator} (${mode.code}) created.`);
        this.refreshData();
      }
    });
  }

  private updateFocusedComponentDesignator(componentDesignator: string): void {
    const next = componentDesignator.trim();
    if (!next || this.focusedComponentDesignator() === next) {
      return;
    }

    this.focusedComponentDesignator.set(next);
    //  Sync the worksheet rows with PDF/Graph canvas clicks
    const currentSelectedRowId = this.store.selectedWorksheetRowId();
    const currentSelectedRow = this.worksheetRows().find(
      (row) => row.failureInstanceId === currentSelectedRowId
    );
    // Only override the worksheet state if it isn't already focused on this component
    if (!currentSelectedRow || currentSelectedRow.componentDesignator !== next) {
      const firstMatchingRow = this.worksheetRows().find(
        (row) => row.componentDesignator === next
      );

      if (firstMatchingRow) {
        this.store.selectedWorksheetRowId.set(firstMatchingRow.failureInstanceId);
      }
    }

  }

  private publishPdfWindowSnapshot(annotations = this.pdfAnnotationStateService.annotations$.value): void {
    this.pdfWindowSyncService.publishSnapshot({
      pdfUrl: this.smartPdfUrl(),
      annotations,
      focusedDesignator: this.focusedComponentDesignator()
    });
  }



  createComponentType(): void {
    const value = this.createComponentTypeForm.getRawValue();
    const payload = {
      name: value.name,
      failure_modes: value.failureModes.map((mode) => ({
        code: mode.code,
        name: mode.failureModeName,
        thermal_model_id: mode.thermalModelId,
        base_rate_value: mode.baseRateValue,
        base_rate_unit: mode.baseRateUnit,
        standard_name: mode.standardName
      }))
    };

    this.apiService.createComponentType(payload).subscribe({
      next: () => {
        this.actionMessage.set('Component type created.');
        this.resetCreateComponentTypeForm();
        this.closeCreateEntityDialog();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create component type.');
      }
    });
  }

  get createComponentTypeFailureModes(): FormArray {
    return this.createComponentTypeForm.controls.failureModes;
  }

  addCreateTypeFailureMode(): void {
    this.createComponentTypeFailureModes.push(this.createFailureModeFormGroup());
  }

  removeCreateTypeFailureMode(index: number): void {
    if (this.createComponentTypeFailureModes.length <= 1) {
      return;
    }

    this.createComponentTypeFailureModes.removeAt(index);
  }

  private resetCreateComponentTypeForm(): void {
    this.createComponentTypeForm.controls.name.reset('');

    while (this.createComponentTypeFailureModes.length > 0) {
      this.createComponentTypeFailureModes.removeAt(0);
    }

    this.createComponentTypeFailureModes.push(this.createFailureModeFormGroup());
  }

  createComponent(): void {
    const value = this.createComponentForm.getRawValue();
    const payload = {
      component_designator: value.componentDesignator,
      component_type_name: value.componentTypeName,
      op_temp: value.opTemp
    };

    this.apiService.createComponent(payload).subscribe({
      next: () => {
        this.actionMessage.set('Component created.');
        this.createComponentForm.reset({ componentDesignator: '', componentTypeName: '', opTemp: 25 });
        this.closeCreateEntityDialog();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create component.');
      }
    });
  }

  createFailureLink(): void {
    const value = this.createFailureLinkForm.getRawValue();
    const payload: {
      component_designator: string;
      failure_mode_code: string;
      failure_effect_id: string;
      system_effect_id: string;
      fi_name: string;
      fi_id?: string;
    } = {
      component_designator: value.componentDesignator,
      failure_mode_code: value.failureModeCode,
      failure_effect_id: value.failureEffectId,
      system_effect_id: value.systemEffectId,
      fi_name: value.fiName
    };

    const requestedId = value.fiId.trim();
    if (requestedId) {
      payload.fi_id = requestedId;
    }

    this.apiService.createFailureLink(payload).subscribe({
      next: () => {
        this.actionMessage.set('Failure link created.');
        this.createFailureLinkForm.reset({
          componentDesignator: '',
          failureModeCode: '',
          failureEffectId: '',
          fiId: '',
          fiName: '',
          systemEffectId: ''
        });
        this.closeCreateEntityDialog();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create failure link.');
      }
    });
  }

  createFailureInstance(): void {
    const value = this.createFailureInstanceForm.getRawValue();
    const payload: {
      component_designator: string;
      failure_mode_code: string;
      fi_name: string;
      fi_id?: string;
    } = {
      component_designator: value.componentDesignator,
      failure_mode_code: value.failureModeCode,
      fi_name: value.fiName
    };

    const requestedId = value.fiId.trim();
    const failureEffectId = value.failureEffectId.trim();
    const systemEffectId = value.systemEffectId.trim();

    if (requestedId) {
      payload.fi_id = requestedId;
    }

    this.apiService.createFailureInstance(payload).subscribe({
      next: (response: ApiResponse<{ message: string; failure_instance_id: string }>) => {
        const createdFailureInstanceId = response.data.failure_instance_id;

        const finish = () => {
          this.actionMessage.set('Failure instance created.');
          this.createFailureInstanceForm.reset({
            componentDesignator: '',
            failureModeCode: '',
            failureEffectId: '',
            systemEffectId: '',
            fiId: '',
            fiName: ''
          });
          this.closeFailureInstanceDialogs();
          this.refreshData();
        };

        if (!failureEffectId) {
          finish();
          return;
        }

        this.apiService.updateFailureInstanceCause({
          failureInstanceId: createdFailureInstanceId,
          failureEffectId
        }).subscribe({
          next: () => {
            if (!systemEffectId) {
              finish();
              return;
            }

            this.apiService.updateFailureEffectLink({
              failureEffectId,
              systemEffectId
            }).subscribe({
              next: () => {
                finish();
              },
              error: (err: unknown) => {
                this.error.set(err instanceof Error ? err.message : 'Failed to link failure effect to system effect.');
              }
            });
          },
          error: (err: unknown) => {
            this.error.set(err instanceof Error ? err.message : 'Failed to link failure instance to failure effect.');
          }
        });
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create failure instance.');
      }
    });
  }

  createSafetyMechanism(): void {
    const value = this.createSafetyMechanismForm.getRawValue();
    const payload: {
      name: string;
      failure_instance_id: string;
      diagnostic_coverage: number;
      mechanism_type: string;
      sm_id?: string;
    } = {
      name: value.name,
      failure_instance_id: value.failureInstanceId,
      diagnostic_coverage: value.diagnosticCoverage,
      mechanism_type: value.mechanismType
    };

    const requestedId = value.smId.trim();
    if (requestedId) {
      payload.sm_id = requestedId;
    }

    this.apiService.createSafetyMechanism(payload).subscribe({
      next: () => {
        this.actionMessage.set('Safety mechanism created.');
        this.createSafetyMechanismForm.reset({
          smId: '',
          name: '',
          failureInstanceId: '',
          diagnosticCoverage: 0.8,
          mechanismType: 'SPF'
        });
        this.closeCreateEntityDialog();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create safety mechanism.');
      }
    });
  }

  updateComponentTemp(): void {
    const value = this.editComponentForm.getRawValue();
    this.apiService.updateComponentTemp(value)
      .subscribe({
        next: () => {
          this.actionMessage.set('Component temperature updated.');
          this.showEditComponentTempDialog.set(false);
          this.refreshData();
        },
        error: (err: unknown) => {
          this.error.set(err instanceof Error ? err.message : 'Failed to update component.');
        }
      });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showComponentContextMenu()) {
      this.closeComponentContextMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    this.closeComponentContextMenu();

    if (this.showCreateEntityDialog()) {
      this.closeCreateEntityDialog();
      return;
    }
    if (this.showAssignTypeDialog()) {
      this.closeAssignTypeDialog();
      return;
    }
    if (this.showAddFailureInstanceDialog()) {
      this.closeAddFailureInstanceDialog();
      return;
    }
    if (this.showEditComponentTempDialog()) {
      this.closeEditComponentTempDialog();
    }
    if (this.showEditCoverageDialog()) {
      this.closeEditCoverageDialog();
    }
    if (this.showEditFailureLinkDialog()) {
      this.closeEditFailureLinkDialog();
    }
    if (this.showEditSafetyMechanismDialog()) {
      this.closeEditSafetyMechanismDialog();
    }
    if (this.showEditFeLinkDialog()) {
      this.closeEditFeLinkDialog();
    }
    if (this.showEditSeLinkDialog()) {
      this.closeEditSeLinkDialog();
    }
    if (this.showEditFiCauseDialog()) {
      this.closeEditFiCauseDialog();
    }
  }

  assignComponentType(): void {
    const value = this.assignTypeForm.getRawValue();
    const componentDesignator = value.componentDesignator.trim();
    const componentTypeName = value.componentTypeName.trim();
    const targetComponentDesignators = this.getAssignTargetComponentDesignators();

    if (!componentDesignator || !componentTypeName || targetComponentDesignators.length === 0) {
      this.error.set('Component and component type are required.');
      return;
    }

    this.error.set('');
    if (targetComponentDesignators.length === 1) {
      this.apiService.assignComponentType(componentDesignator, componentTypeName, value.opTemp).subscribe({
        next: () => {
          this.actionMessage.set(`Component type "${componentTypeName}" assigned to ${componentDesignator}.`);
          this.closeAssignEditorDialogs();
          this.resetAssignTypeForm();
          this.refreshData();
        },
        error: (err: unknown) => {
          this.error.set(err instanceof Error ? err.message : 'Failed to assign component type.');
        }
      });
      return;
    }

    this.apiService.bulkAssignComponentType(targetComponentDesignators, componentTypeName, value.opTemp).subscribe({
      next: () => {
        this.actionMessage.set(`Component type "${componentTypeName}" assigned to ${targetComponentDesignators.length} components.`);
        this.closeAssignEditorDialogs();
        this.resetAssignTypeForm();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to assign component type.');
      }
    });
  }

  createSafetyGoal(): void {
    const value = this.createSafetyGoalForm.getRawValue();
    const payload: { description: string; id?: string } = {
      description: value.description.trim()
    };

    const requestedId = value.id.trim();
    if (requestedId) {
      payload.id = requestedId;
    }

    this.apiService.createSafetyGoal(payload).subscribe({
      next: (response: ApiResponse<{ message: string; id: string }>) => {
        this.actionMessage.set(`Safety goal ${response.data.id} created.`);
        this.createSafetyGoalForm.reset({ id: '', description: '' });
        this.closeCreateEntityDialog();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create safety goal.');
      }
    });
  }

  createSystemEffect(): void {
    const value = this.createSystemEffectForm.getRawValue();
    const returnContext = this.createEntityReturnContext;
    const payload: { name: string; safety_goal_id: string; id?: string } = {
      name: value.name.trim(),
      safety_goal_id: value.safetyGoalId.trim()
    };

    const requestedId = value.id.trim();
    if (requestedId) {
      payload.id = requestedId;
    }

    this.apiService.createSystemEffect(payload).subscribe({
      next: (response: ApiResponse<{ message: string; id: string }>) => {
        if (returnContext?.parent === 'add-failure-instance' && returnContext.field === 'systemEffectId') {
          this.createFailureInstanceForm.patchValue({ systemEffectId: response.data.id });
        }

        if (returnContext?.parent === 'create-failure-effect' && returnContext.field === 'systemEffectId') {
          this.createFailureEffectForm.patchValue({ systemEffectId: response.data.id });
        }

        this.actionMessage.set(`System effect ${response.data.id} created.`);
        this.createSystemEffectForm.reset({ id: '', name: '', safetyGoalId: '' });
        this.closeCreateEntityDialog();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create system effect.');
      }
    });
  }

  createFailureEffect(): void {
    const value = this.createFailureEffectForm.getRawValue();
    const returnContext = this.createEntityReturnContext;
    const payload: { name: string; system_effect_id: string; id?: string } = {
      name: value.name.trim(),
      system_effect_id: value.systemEffectId.trim()
    };

    const requestedId = value.id.trim();
    if (requestedId) {
      payload.id = requestedId;
    }

    this.apiService.createFailureEffect(payload).subscribe({
      next: (response: ApiResponse<{ message: string; id: string }>) => {
        if (returnContext?.parent === 'add-failure-instance' && returnContext.field === 'failureEffectId') {
          this.createFailureInstanceForm.patchValue({
            failureEffectId: response.data.id,
            systemEffectId: value.systemEffectId.trim()
          });
        }

        this.actionMessage.set(`Failure effect ${response.data.id} created.`);
        this.createFailureEffectForm.reset({ id: '', name: '', systemEffectId: '' });
        this.closeCreateEntityDialog();
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to create failure effect.');
      }
    });
  }

  updateCoverage(): void {
    const value = this.editCoverageForm.getRawValue();
    this.apiService.updateCoverage(value).subscribe({
      next: () => {
        this.actionMessage.set('Diagnostic coverage updated.');
        this.showEditCoverageDialog.set(false);
        this.showEditSafetyMechanismDialog.set(false);
        this.refreshData();
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to update coverage.');
      }
    });
  }

  updateFailureEffectLink(): void {
    const value = this.editFeLink.getRawValue();
    this.apiService.updateFailureEffectLink(value)
      .subscribe({
        next: () => {
          this.actionMessage.set('Failure effect link updated.');
          this.showEditFeLinkDialog.set(false);
          this.refreshData();
        },
        error: (err: unknown) => {
          this.error.set(err instanceof Error ? err.message : 'Failed to update failure effect link.');
        }
      });
  }

  updateSystemEffectLink(): void {
    const value = this.editSeLink.getRawValue();
    this.apiService.updateSystemEffectLink(value)
      .subscribe({
        next: () => {
          this.actionMessage.set('System effect link updated.');
          this.showEditSeLinkDialog.set(false);
          this.refreshData();
        },
        error: (err: unknown) => {
          this.error.set(err instanceof Error ? err.message : 'Failed to update system effect link.');
        }
      });
  }

  updateFailureInstanceCause(): void {
    const value = this.editFiCause.getRawValue();
    this.apiService.updateFailureInstanceCause(value)
      .subscribe({
        next: () => {
          this.actionMessage.set('Failure instance cause updated.');
          this.showEditFiCauseDialog.set(false);
          this.refreshData();
        },
        error: (err: unknown) => {
          this.error.set(err instanceof Error ? err.message : 'Failed to update failure instance cause.');
        }
      });
  }

  updateFailureLinkRecord(): void {
    const value = this.editFailureLinkForm.getRawValue();
    this.apiService.updateFailureInstanceCause({
      failureInstanceId: value.failureInstanceId,
      failureEffectId: value.failureEffectId
    }).subscribe({
      next: () => {
        if (!value.failureEffectId) {
          this.actionMessage.set('Failure link updated.');
          this.closeEditFailureLinkDialog();
          this.refreshData();
          return;
        }

        this.apiService.updateFailureEffectLink({
          failureEffectId: value.failureEffectId,
          systemEffectId: value.systemEffectId
        }).subscribe({
          next: () => {
            if (!value.systemEffectId) {
              this.actionMessage.set('Failure link updated.');
              this.closeEditFailureLinkDialog();
              this.refreshData();
              return;
            }

            this.apiService.updateSystemEffectLink({
              systemEffectId: value.systemEffectId,
              safetyGoalId: value.safetyGoalId
            }).subscribe({
              next: () => {
                this.actionMessage.set('Failure link updated.');
                this.closeEditFailureLinkDialog();
                this.refreshData();
              },
              error: (err: unknown) => {
                this.error.set(err instanceof Error ? err.message : 'Failed to update failure link safety goal.');
              }
            });
          },
          error: (err: unknown) => {
            this.error.set(err instanceof Error ? err.message : 'Failed to update failure link system effect.');
          }
        });
      },
      error: (err: unknown) => {
        this.error.set(err instanceof Error ? err.message : 'Failed to update failure link failure effect.');
      }
    });
  }

  syncFeLinkSelection(): void {
    const feId = this.editFeLink.controls.failureEffectId.value;
    const fe = this.failureEffects().find((e: FailureEffectOption) => e.id === feId);
    this.editFeLink.patchValue({ systemEffectId: fe?.system_effect_id ?? '' });
  }

  syncSeLinkSelection(): void {
    const seId = this.editSeLink.controls.systemEffectId.value;
    const se = this.systemEffects().find((e: SystemEffectOption) => e.id === seId);
    this.editSeLink.patchValue({ safetyGoalId: se?.safety_goal_id ?? '' });
  }

  syncFiCauseSelection(): void {
    const fiId = this.editFiCause.controls.failureInstanceId.value;
    const fi = this.failureInstances().find((e: FailureInstanceOption) => e.id === fiId);
    this.editFiCause.patchValue({ failureEffectId: fi?.failure_effect_id ?? '' });
  }

  syncFailureModeOptions(): void {
    const componentDesignator = this.createFailureLinkForm.controls.componentDesignator.value;
    const failureModeCode = this.createFailureLinkForm.controls.failureModeCode.value;
    const options = this.businessLogic.getRemainingFailureModesForComponent(componentDesignator);
    if (!options.some((option) => option.code === failureModeCode)) {
      this.createFailureLinkForm.patchValue({ failureModeCode: options[0]?.code ?? '' });
    }

    this.updateCreateFailureLinkName();
  }

  syncFailureInstanceModeOptions(): void {
    const componentDesignator = this.createFailureInstanceForm.controls.componentDesignator.value;
    const failureModeCode = this.createFailureInstanceForm.controls.failureModeCode.value;
    const options = this.businessLogic.getRemainingFailureModesForComponent(componentDesignator);
    if (!options.some((option) => option.code === failureModeCode)) {
      this.createFailureInstanceForm.patchValue({ failureModeCode: options[0]?.code ?? '' });
    }

    this.updateCreateFailureInstanceName();
  }

  onCreateFailureLinkModeChanged(): void {
    this.updateCreateFailureLinkName();
  }

  onCreateFailureInstanceModeChanged(): void {
    this.updateCreateFailureInstanceName();
  }

  private updateCreateFailureLinkName(): void {
    const componentDesignator = this.createFailureLinkForm.controls.componentDesignator.value;
    const failureModeCode = this.createFailureLinkForm.controls.failureModeCode.value;

    if (!componentDesignator || !failureModeCode) {
      this.createFailureLinkForm.patchValue({ fiName: '' });
      return;
    }

    this.createFailureLinkForm.patchValue({
      fiName: this.businessLogic.buildFailureInstanceName(componentDesignator, failureModeCode)
    });
  }

  private updateCreateFailureInstanceName(): void {
    const componentDesignator = this.createFailureInstanceForm.controls.componentDesignator.value;
    const failureModeCode = this.createFailureInstanceForm.controls.failureModeCode.value;

    if (!componentDesignator || !failureModeCode) {
      this.createFailureInstanceForm.patchValue({ fiName: '' });
      return;
    }

    this.createFailureInstanceForm.patchValue({
      fiName: this.businessLogic.buildFailureInstanceName(componentDesignator, failureModeCode)
    });
  }

  syncFailureEffectSystemEffect(): void {
    const failureEffectId = this.createFailureLinkForm.controls.failureEffectId.value;
    const selectedEffect = this.failureEffects().find((effect: FailureEffectOption) => effect.id === failureEffectId);
    if (selectedEffect?.system_effect_id) {
      this.createFailureLinkForm.patchValue({ systemEffectId: selectedEffect.system_effect_id });
    }
  }

  syncCoverageSelection(): void {
    const selectedLinkKey = this.editCoverageForm.controls.coverageLinkKey.value;
    const selectedLink = this.coverageLinks().find(
      (link: CoverageLink) => `${link.safety_mechanism_id}::${link.failure_instance_id}` === selectedLinkKey
    );

    if (!selectedLink) {
      return;
    }

    this.editCoverageForm.patchValue({
      safetyMechanismId: selectedLink.safety_mechanism_id,
      failureInstanceId: selectedLink.failure_instance_id,
      diagnosticCoverage: selectedLink.diagnostic_coverage,
      mechanismType: selectedLink.mechanism_type
    });
  }

  get filteredAssignableComponents(): ComponentSummary[] {
    const query = this.assignTypeSearch.trim().toLowerCase();
    return [...this.components()]
      .filter((component: ComponentSummary) => !query || this.buildAssignableComponentSearchText(component).includes(query))
      .sort((left: ComponentSummary, right: ComponentSummary) => {
        const leftAssigned = Boolean(left.component_type);
        const rightAssigned = Boolean(right.component_type);
        if (leftAssigned !== rightAssigned) {
          return leftAssigned ? 1 : -1;
        }
        return left.component_designator.localeCompare(right.component_designator);
      });
  }

  get filteredCreateComponentTypes(): ComponentType[] {
    return this.sortCreateItems(
      this.componentTypes().filter((type: ComponentType) =>
        this.matchesCreateListSearch(type.name, ...type.failure_modes.map((mode) => `${mode.code} ${mode.name}`))
      ),
      (type: ComponentType) => type.name
    );
  }

  get filteredCreateComponents(): ComponentSummary[] {
    return this.sortCreateItems(
      this.components().filter((component: ComponentSummary) =>
        this.matchesCreateListSearch(
          component.component_designator,
          component.component_type,
          component.part_number,
          component.package,
          component.op_temp
        )
      ),
      (component: ComponentSummary) => component.component_designator
    );
  }

  get filteredCreateFailureInstances(): FailureInstanceOption[] {
    return this.sortCreateItems(
      this.failureInstances().filter((fi: FailureInstanceOption) =>
        this.matchesCreateListSearch(
          fi.id,
          fi.name,
          fi.component_designator,
          fi.failure_mode_code,
          fi.failure_effect_id,
          fi.system_effect_id,
          fi.safety_goal_id
        )
      ),
      (fi: FailureInstanceOption) => fi.id
    );
  }

  get filteredCreateSafetyMechanisms(): SafetyMechanismOption[] {
    return this.sortCreateItems(
      this.safetyMechanisms().filter((mechanism: SafetyMechanismOption) =>
        this.matchesCreateListSearch(
          mechanism.id,
          mechanism.name,
          mechanism.mechanism_type,
          ...mechanism.failure_instance_ids,
          ...mechanism.latent_failure_instance_ids
        )
      ),
      (mechanism: SafetyMechanismOption) => mechanism.id
    );
  }

  get filteredCreateSafetyGoals(): SafetyGoal[] {
    return this.sortCreateItems(
      this.safetyGoals().filter((goal: SafetyGoal) => this.matchesCreateListSearch(goal.id, goal.description)),
      (goal: SafetyGoal) => goal.id
    );
  }

  get filteredCreateSystemEffects(): SystemEffectOption[] {
    return this.sortCreateItems(
      this.systemEffects().filter((effect: SystemEffectOption) =>
        this.matchesCreateListSearch(effect.id, effect.name, effect.safety_goal_id)
      ),
      (effect: SystemEffectOption) => effect.id
    );
  }

  get filteredCreateFailureEffects(): FailureEffectOption[] {
    return this.sortCreateItems(
      this.failureEffects().filter((effect: FailureEffectOption) =>
        this.matchesCreateListSearch(effect.id, effect.name, effect.system_effect_id)
      ),
      (effect: FailureEffectOption) => effect.id
    );
  }

  get selectedAssignableComponent(): ComponentSummary | null {
    const selectedDesignator = this.assignTypeForm.controls.componentDesignator.value.trim();
    return this.components().find((component: ComponentSummary) => component.component_designator === selectedDesignator) ?? null;
  }

  get identicalAssignableComponents(): ComponentSummary[] {
    const selectedComponent = this.selectedAssignableComponent;
    const identicalKey = this.getIdenticalPartKey(selectedComponent);
    if (!selectedComponent || !identicalKey) {
      return [];
    }

    return this.components()
      .filter((component: ComponentSummary) => component.component_designator !== selectedComponent.component_designator && this.getIdenticalPartKey(component) === identicalKey)
      .sort((left: ComponentSummary, right: ComponentSummary) => left.component_designator.localeCompare(right.component_designator));
  }

  get selectedAssignableTargetCount(): number {
    return this.getAssignTargetComponentDesignators().length;
  }

  onAssignTypeSearchChange(value: string): void {
    this.assignTypeSearch = value;
  }

  selectAssignableComponent(componentDesignator: string): void {
    this.assignTypeForm.patchValue({ componentDesignator });
  }

  onAssignComponentSelectionChange(): void {
    const selectedComponent = this.selectedAssignableComponent;
    this.selectedIdenticalComponentDesignators = [];

    if (!selectedComponent) {
      this.assignTypeForm.patchValue({ opTemp: 25 });
      return;
    }

    const patch: { componentTypeName?: string; opTemp: number } = {
      opTemp: selectedComponent.op_temp ?? 25
    };

    if (selectedComponent.component_type) {
      patch.componentTypeName = selectedComponent.component_type;
    }

    this.assignTypeForm.patchValue(patch);
  }

  isIdenticalComponentSelected(componentDesignator: string): boolean {
    return this.selectedIdenticalComponentDesignators.includes(componentDesignator);
  }

  toggleIdenticalComponent(componentDesignator: string, checked: boolean): void {
    if (checked) {
      this.selectedIdenticalComponentDesignators = [...new Set([...this.selectedIdenticalComponentDesignators, componentDesignator])];
      return;
    }

    this.selectedIdenticalComponentDesignators = this.selectedIdenticalComponentDesignators.filter((component_designator) => component_designator !== componentDesignator);
  }

  areAllIdenticalPartsSelected(): boolean {
    const identicalIds = this.identicalAssignableComponents.map((component: ComponentSummary) => component.component_designator);
    return identicalIds.length > 0 && identicalIds.every((componentDesignator) => this.selectedIdenticalComponentDesignators.includes(componentDesignator));
  }

  toggleAllIdenticalParts(checked: boolean): void {
    this.selectedIdenticalComponentDesignators = checked
      ? this.identicalAssignableComponents.map((component: ComponentSummary) => component.component_designator)
      : [];
  }

  private applySuggestedIds(force = false): void {
    const safetyGoalId = this.createSafetyGoalForm.controls.id.value.trim();
    const systemEffectId = this.createSystemEffectForm.controls.id.value.trim();
    const failureEffectId = this.createFailureEffectForm.controls.id.value.trim();
    const createFailureInstanceId = this.createFailureInstanceForm.controls.fiId.value.trim();
    const failureInstanceId = this.createFailureLinkForm.controls.fiId.value.trim();
    const safetyMechanismId = this.createSafetyMechanismForm.controls.smId.value.trim();

    if (force || !safetyGoalId) {
      this.createSafetyGoalForm.patchValue({ id: this.idSuggestions().safety_goal_id });
    }

    if (force || !systemEffectId) {
      this.createSystemEffectForm.patchValue({ id: this.idSuggestions().system_effect_id });
    }

    if (force || !failureEffectId) {
      this.createFailureEffectForm.patchValue({ id: this.idSuggestions().failure_effect_id });
    }

    if (force || !createFailureInstanceId) {
      this.createFailureInstanceForm.patchValue({ fiId: this.idSuggestions().failure_instance_id });
    }

    if (force || !failureInstanceId) {
      this.createFailureLinkForm.patchValue({ fiId: this.idSuggestions().failure_instance_id });
    }

    if (force || !safetyMechanismId) {
      this.createSafetyMechanismForm.patchValue({ smId: this.idSuggestions().safety_mechanism_id });
    }
  }

  private resetAssignTypeForm(): void {
    this.assignTypeSearch = '';
    this.selectedIdenticalComponentDesignators = [];
    this.assignTypeForm.reset({ componentDesignator: '', componentTypeName: '', opTemp: 25 });
  }

  private closeAssignEditorDialogs(): void {
    if (this.showCreateEntityDialog()) {
      this.closeCreateEntityDialog();
    }
    if (this.showAssignTypeDialog()) {
      this.closeAssignTypeDialog();
    }
  }

  private closeFailureInstanceDialogs(): void {
    if (this.showCreateEntityDialog()) {
      this.closeCreateEntityDialog();
    }
    if (this.showAddFailureInstanceDialog()) {
      this.closeAddFailureInstanceDialog();
    }
  }

  private getAssignTargetComponentDesignators(): string[] {
    const selectedComponentDesignator = this.assignTypeForm.controls.componentDesignator.value.trim();
    if (!selectedComponentDesignator) {
      return [];
    }

    return [selectedComponentDesignator, ...this.selectedIdenticalComponentDesignators].filter(
      (componentDesignator, index, values) => componentDesignator !== '' && values.indexOf(componentDesignator) === index
    );
  }

  private reconcileAssignSelection(): void {
    const selectedComponent = this.selectedAssignableComponent;
    if (!selectedComponent && this.assignTypeForm.controls.componentDesignator.value.trim()) {
      this.resetAssignTypeForm();
      return;
    }

    const validIdenticalIds = new Set(this.identicalAssignableComponents.map((component: ComponentSummary) => component.component_designator));
    this.selectedIdenticalComponentDesignators = this.selectedIdenticalComponentDesignators.filter((componentDesignator) => validIdenticalIds.has(componentDesignator));
  }

  private buildAssignableComponentSearchText(component: ComponentSummary): string {
    return [
      component.component_designator,
      component.package,
      component.part_number,
      component.component_type
    ]
      .filter((value): value is string => Boolean(value))
      .join(' ')
      .toLowerCase();
  }

  private getIdenticalPartKey(component: ComponentSummary | null): string | null {
    if (!component) {
      return null;
    }

    const partNumber = component.part_number?.trim().toLowerCase();
    if (!partNumber) {
      return null;
    }

    const packageName = component.package?.trim().toLowerCase();
    return packageName ? `${partNumber}::${packageName}` : partNumber;
  }

  private matchesCreateListSearch(...values: Array<string | number | null | undefined>): boolean {
    const query = this.createListSearch.trim().toLowerCase();
    if (!query) {
      return true;
    }

    return values.some((value) => String(value ?? '').toLowerCase().includes(query));
  }

  private sortCreateItems<T>(items: T[], getKey: (item: T) => string): T[] {
    const direction = this.createListSortDirection;
    return [...items].sort((left, right) => {
      const cmp = getKey(left).localeCompare(getKey(right), undefined, { sensitivity: 'base' });
      return direction === 'asc' ? cmp : -cmp;
    });
  }

}
